import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Progress } from '../ui/progress';
import { Globe, ArrowLeft, Lock, CheckCircle, Circle, Award, Flame, Trophy } from 'lucide-react';
import { programAPI } from '../../services/programApi';
import { toast } from '../../hooks/use-toast';

const DayOverview = () => {
  const navigate = useNavigate();
  const { language } = useParams();
  const [progress, setProgress] = useState(null);
  const [curriculum, setCurriculum] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRank, setUserRank] = useState(null);
  const isLoggedIn = !!localStorage.getItem('authToken');

  useEffect(() => {
    fetchData();
  }, [language]);

  const fetchData = async () => {
    try {
      if (isLoggedIn) {
        const [progressData, curriculumData] = await Promise.all([
          programAPI.getProgress(language),
          programAPI.getCurriculum(language)
        ]);
        setProgress(progressData);
        setCurriculum(curriculumData.lessons);
        try {
          const rankData = await programAPI.getUserRank(language);
          setUserRank(rankData);
        } catch (e) {}
      } else {
        const curriculumData = await programAPI.getCurriculum(language);
        setCurriculum(curriculumData.lessons);
        setProgress({ current_day: 1, completed_days: [], streak: 0 });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load program data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const isDayUnlocked = (dayNumber) => {
    return dayNumber <= (progress?.current_day || 1);
  };

  const isDayCompleted = (dayNumber) => {
    return progress?.completed_days?.includes(dayNumber);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading program...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/program')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <Globe className="h-8 w-8 text-amber-600" />
                <span className="text-xl font-bold text-slate-900">
                  {language.charAt(0).toUpperCase() + language.slice(1)} Program
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Guest Banner */}
        {!isLoggedIn && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between" data-testid="guest-day-banner">
            <p className="text-amber-800 text-sm font-medium">Preview Day 1 free — sign up to unlock all 15 days and track progress</p>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 ml-4 shrink-0" onClick={() => navigate('/register')}>
              Sign Up Free
            </Button>
          </div>
        )}
        {/* Progress Summary with Streak */}
        <Card className="border-2 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Your Progress</h2>
                <p className="text-slate-600">
                  {progress?.completed_days?.length || 0} of 15 days completed
                </p>
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-amber-600">
                  {progress?.completion_percentage || 0}%
                </p>
                <p className="text-sm text-slate-600">Complete</p>
              </div>
            </div>
            <Progress value={progress?.completion_percentage || 0} className="h-3 mb-4" />
            
            {/* Streak and Rank Row */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center space-x-6">
                {/* Streak */}
                <div className="flex items-center space-x-2">
                  <Flame className={`h-6 w-6 ${progress?.streak_days > 0 ? 'text-orange-500' : 'text-slate-300'}`} />
                  <div>
                    <p className="text-lg font-bold text-slate-900">{progress?.streak_days || 0}</p>
                    <p className="text-xs text-slate-500">Day Streak</p>
                  </div>
                </div>
                
                {/* Rank */}
                {userRank && userRank.rank && (
                  <div className="flex items-center space-x-2">
                    <Trophy className="h-6 w-6 text-yellow-500" />
                    <div>
                      <p className="text-lg font-bold text-slate-900">#{userRank.rank}</p>
                      <p className="text-xs text-slate-500">Leaderboard</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Last Activity */}
              {progress?.last_activity && (
                <p className="text-sm text-slate-500">
                  Last activity: {new Date(progress.last_activity).toLocaleDateString()}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Day Grid */}
        <div className="space-y-4">
          {curriculum.map((day) => {
            const unlocked = isDayUnlocked(day.day_number);
            const completed = isDayCompleted(day.day_number);
            const isCurrent = day.day_number === progress?.current_day;

            return (
              <Card
                key={day.day_number}
                data-testid={`day-card-${day.day_number}`}
                className={`border-2 cursor-pointer transition-all ${
                  completed ? 'border-emerald-300 bg-emerald-50/50' :
                  isCurrent ? 'border-amber-400 shadow-lg' :
                  unlocked ? 'hover:border-amber-300 hover:shadow-md' :
                  'opacity-60 cursor-not-allowed'
                }`}
                onClick={() => unlocked && navigate(`/program/${language}/day/${day.day_number}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        completed ? 'bg-emerald-600' :
                        isCurrent ? 'bg-amber-600' :
                        unlocked ? 'bg-slate-200' :
                        'bg-slate-100'
                      }`}>
                        {completed ? (
                          <CheckCircle className="h-6 w-6 text-white" />
                        ) : unlocked ? (
                          <Circle className="h-6 w-6 text-slate-600" />
                        ) : (
                          <Lock className="h-6 w-6 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">
                          Day {day.day_number}: {day.title}
                        </h3>
                        <p className="text-sm text-slate-600">{day.description}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {day.estimated_time} minutes
                        </p>
                      </div>
                    </div>
                    {unlocked && !completed && (
                      <Button className="bg-amber-600 hover:bg-amber-700">
                        {isCurrent ? 'Continue' : 'Start'}
                      </Button>
                    )}
                    {completed && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-emerald-600 font-semibold">
                          Score: {progress?.daily_test_scores?.[day.day_number] || 'N/A'}/10
                        </span>
                        <Button variant="outline" size="sm">
                          Review
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Final Test Card */}
          {progress?.completed_days?.length === 15 && (
            <Card className="border-2 border-amber-400 bg-gradient-to-r from-amber-50 to-sky-50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Award className="h-12 w-12 text-amber-600" />
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">
                        Final Comprehensive Test
                      </h3>
                      <p className="text-slate-600">
                        Test your knowledge across all 15 days
                      </p>
                    </div>
                  </div>
                  <Button 
                    className="bg-amber-600 hover:bg-amber-700"
                    onClick={() => navigate(`/program/${language}/final-test`)}
                  >
                    Take Final Test
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DayOverview;